import Aggregated from '@/assets/svg/sessions/Aggregated.svg'
import AggregationFailed from '@/assets/svg/sessions/AggregationFailed.svg'
import Approved from '@/assets/svg/sessions/Approved.svg'
import CdrCreationFailed from '@/assets/svg/sessions/CdrCreationFailed.svg'
import CDRPublished from '@/assets/svg/sessions/CDRPublished.svg'
import Invalid from '@/assets/svg/sessions/Invalid.svg'
import PendingAggregation from '@/assets/svg/sessions/PendingAggregation.svg'
import PendingRerate from '@/assets/svg/sessions/PendingRerate.svg'
import PendingUndoAggregation from '@/assets/svg/sessions/PendingUndoAggregation.svg'
import Rejected from '@/assets/svg/sessions/Rejected.svg'
import Rerating from '@/assets/svg/sessions/Rerating.svg'
import Started from '@/assets/svg/sessions/Started.svg'
import Suspiscious from '@/assets/svg/sessions/Suspiscious.svg'
import TxActive from '@/assets/svg/sessions/TxActive.svg'
import TxStopped from '@/assets/svg/sessions/TxStopped.svg'
import Validating from '@/assets/svg/sessions/Validating.svg'
import { ExceptionSeverityType } from '@/enums/ExceptionSeverityType'
import { PayterSessionStatus } from '@/enums/Session/PayterSessionStatus'
import { SessionState } from '@/enums/SessionState'
import { startOfDay, startOfHour, startOfMinute, startOfMonth, startOfSecond, startOfWeek } from 'date-fns'

export function getSessionStateStatusImage(status: SessionState | undefined) {
  const sessionStateImageMap: Record<SessionState, string> = {
    [SessionState.Aggregated]: Aggregated,
    [SessionState.Approved]: Approved,
    [SessionState.CdrPublished]: CDRPublished,
    [SessionState.Rejected]: Rejected,
    [SessionState.Started]: Started,
    [SessionState.TxStopped]: TxStopped,
    [SessionState.Suspicious]: Suspiscious,
    [SessionState.TxActive]: TxActive,
    [SessionState.CdrCreationFailed]: CdrCreationFailed,
    [SessionState.Invalid]: Invalid,
    [SessionState.PendingAggregation]: PendingAggregation,
    [SessionState.PendingRerate]: PendingRerate,
    [SessionState.PendingUndoAggregation]: PendingUndoAggregation,
    [SessionState.Rerating]: Rerating,
    [SessionState.Validating]: Validating,
    [SessionState.AggregationFailed]: AggregationFailed
  }
  return status !== undefined && sessionStateImageMap[status] !== undefined ? sessionStateImageMap[status] : undefined
}

export function getSessionStateLabel(state: SessionState | undefined) {
  const stateLabelMap = {
    [SessionState.Aggregated]: 'aggregated',
    [SessionState.TxActive]: 'txActive',
    [SessionState.TxStopped]: 'txStopped',
    [SessionState.Suspicious]: 'suspicious',
    [SessionState.Approved]: 'approved',
    [SessionState.Rejected]: 'rejected',
    [SessionState.CdrPublished]: 'cdrPublished',
    [SessionState.AggregationFailed]: 'aggregationFailed',
    [SessionState.CdrCreationFailed]: 'cdrCreationFailed',
    [SessionState.Invalid]: 'invalid',
    [SessionState.PendingAggregation]: 'pendingAggregation',
    [SessionState.PendingRerate]: 'pendingRerate',
    [SessionState.PendingUndoAggregation]: 'pendingUndoAggregation',
    [SessionState.Rerating]: 'rerating',
    [SessionState.Validating]: 'validating',
    [SessionState.Started]: 'started'
  }
  return state !== undefined && stateLabelMap[state] !== undefined ? stateLabelMap[state] : 'unknown'
}

export function getSeverityLabel(severity: ExceptionSeverityType | undefined) {
  const severityLabelMap = {
    [ExceptionSeverityType.ERROR]: 'error',
    [ExceptionSeverityType.INFO]: 'info',
    [ExceptionSeverityType.WARNING]: 'warning'
  }

  return severity !== undefined && severityLabelMap[severity] !== undefined ? severityLabelMap[severity] : undefined
}

export type MultiAxisGraphPoint = {
  Interval: Date
  IntervalStop: number | null
  MeterValue: number | null
  StateOfChargeValue: number | null
}

export type TimeUnit = 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month'
export const timeFormat = 'HH:mm:ss'
export const timeFormatDay = 'dd/MM'
export const timeFormatMonth = 'MM/yyyy'

export function getDynamicTimeScaleConfig(start: Date, end: Date) {
  const totalSeconds = (end.getTime() - start.getTime()) / 1000
  const totalDays = totalSeconds / 86400
  let unitResult: TimeUnit
  const secondFactor = 3600

  if (totalSeconds <= secondFactor) {
    unitResult = 'second'
    return { unit: unitResult, stepSize: 60, format: timeFormat }
  } else if (totalSeconds <= 6 * secondFactor) {
    unitResult = 'minute'
    return { unit: unitResult, stepSize: 10, format: timeFormat }
  } else if (totalSeconds <= 24 * secondFactor) {
    unitResult = 'minute'
    return { unit: unitResult, stepSize: 30, format: timeFormat }
  } else if (totalDays <= 2) {
    unitResult = 'hour'
    return { unit: unitResult, stepSize: 6, format: timeFormat }
  } else if (totalDays <= 30) {
    unitResult = 'day'
    return { unit: unitResult, stepSize: 1, format: timeFormatDay }
  } else if (totalDays <= 90) {
    unitResult = 'week'
    return { unit: unitResult, stepSize: 1, format: timeFormatDay }
  } else {
    unitResult = 'month'
    return { unit: unitResult, stepSize: 1, format: timeFormatMonth }
  }
}

export function getClosestPointsByStep(points: MultiAxisGraphPoint[], start: Date, end: Date, unit: TimeUnit, valueKey: keyof MultiAxisGraphPoint): MultiAxisGraphPoint[] {
  const grouped = new Map<string, MultiAxisGraphPoint>()

  const getIntervalKey = (date: Date): string => {
    let base: Date
    switch (unit) {
      case 'second':
        base = startOfSecond(date)
        break
      case 'minute':
        base = startOfMinute(date)
        break
      case 'hour':
        base = startOfHour(date)
        break
      case 'day':
        base = startOfDay(date)
        break
      case 'week':
        base = startOfWeek(date, { weekStartsOn: 1 })
        break
      case 'month':
        base = startOfMonth(date)
        break
      default:
        throw new Error(`Unsupported unit: ${unit}`)
    }
    return base.toISOString()
  }

  for (const point of points) {
    const time = new Date(point.Interval)
    if (time < start || time > end) {
      continue
    }

    const key = getIntervalKey(time)
    const currentMax = grouped.get(key)

    if (!currentMax || (point[valueKey] as number) > (currentMax[valueKey] as number)) {
      const newPoint = {
        Interval: new Date(key),
        IntervalStop: point.IntervalStop,
        MeterValue: point.MeterValue,
        StateOfChargeValue: point.StateOfChargeValue
      }
      grouped.set(key, newPoint)
    }
  }

  return Array.from(grouped.values())
}

export function mergePointsByInterval(meterPoints: MultiAxisGraphPoint[], sampleValues: MultiAxisGraphPoint[]): MultiAxisGraphPoint[] {
  const mergedMap = new Map<string, MultiAxisGraphPoint>()

  for (const point of meterPoints) {
    const key = new Date(point.Interval).toISOString()
    mergedMap.set(key, {
      Interval: new Date(point.Interval),
      IntervalStop: point.IntervalStop ?? null,
      MeterValue: point.MeterValue ?? null,
      StateOfChargeValue: null
    })
  }

  for (const point of sampleValues) {
    const key = new Date(point.Interval).toISOString()
    const existing = mergedMap.get(key)
    if (existing) {
      existing.StateOfChargeValue = point.StateOfChargeValue ?? null
    } else {
      mergedMap.set(key, {
        Interval: new Date(point.Interval),
        IntervalStop: point.IntervalStop ?? null,
        MeterValue: null,
        StateOfChargeValue: point.StateOfChargeValue ?? null
      })
    }
  }

  return Array.from(mergedMap.values()).sort((a, b) => new Date(a.Interval).getTime() - new Date(b.Interval).getTime())
}

export function getPayterSessionStatusSeverity(sessionStatus: PayterSessionStatus) {
  switch (sessionStatus) {
    case PayterSessionStatus.Failed:
    case PayterSessionStatus.FailedToCommit:
    case PayterSessionStatus.Unauthorized:
      return 'error'
    case PayterSessionStatus.New:
    case PayterSessionStatus.Started:
    case PayterSessionStatus.Stopped:
    case PayterSessionStatus.Stopping:
    case PayterSessionStatus.Finalizing:
      return 'information'
    case PayterSessionStatus.Cancelled:
    case PayterSessionStatus.UnknownPayterSession:
      return 'warning'
    default:
      return 'success'
  }
}

export function canReAuthRerateSession(sessionState: SessionState): boolean {
  return [SessionState.Approved, SessionState.Suspicious, SessionState.Rejected].includes(sessionState)
}

export function canApproveSession(sessionState: SessionState): boolean {
  return [SessionState.Suspicious, SessionState.Rejected].includes(sessionState)
}

export function canSendReceiptEmailSession(sessionState: SessionState): boolean {
  return [SessionState.Approved, SessionState.Aggregated, SessionState.CdrPublished, SessionState.PendingAggregation].includes(sessionState)
}

export function canRejectSession(sessionState: SessionState): boolean {
  return [SessionState.Approved, SessionState.Suspicious, SessionState.CdrPublished, SessionState.Invalid].includes(sessionState)
}

export function canInvalidateSession(sessionState: SessionState): boolean {
  return [SessionState.Approved, SessionState.Suspicious, SessionState.Rejected, SessionState.CdrPublished].includes(sessionState)
}

const RECENT_SESSION_STATES: SessionState[] = [SessionState.Started, SessionState.TxActive]

export function isRecentSessionMode(dateFrom: Date | null, sessionStates: SessionState[] | null | undefined): boolean {
  if (!dateFrom) {
    return false
  }

  if (!sessionStates || sessionStates === null || sessionStates.length === 0) {
    return false
  }

  const hasOnlyRecentStates = sessionStates.every(state => RECENT_SESSION_STATES.includes(state))
  if (!hasOnlyRecentStates) {
    return false
  }

  const minutesDifference = (Date.now() - dateFrom.getTime()) / (1000 * 60)
  
  if (minutesDifference <= 61) {
    return false
  }

  const daysDifference = minutesDifference / (60 * 24)
  return daysDifference <= 30
}

export function processSessionsDateFilterParams(queryParamStr: string): string {
  const params = new URLSearchParams(queryParamStr)
  const dateFields = ['dateFrom', 'dateUntil']

  dateFields.forEach(field => {
    const value = params.get(field)
    if (value) {
      const date = new Date(value)
      if (!Number.isNaN(date.getTime())) {
        params.set(field, date.toISOString())
      }
    }
  })

  return params.toString()
}
